// src/pages/Products/ProductForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { productService, partTypeService } from '../../services/api';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

// Import custom components
import PartRequirementsSection from './PartRequirementsSection';
import CustomVariantsSection from './CustomVariantsSection';

const ProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [partTypes, setPartTypes] = useState([]);
    const [error, setError] = useState(null);
    const [customVariants, setCustomVariants] = useState([]);
    const [newVariantName, setNewVariantName] = useState('');
    const [showNewVariantInput, setShowNewVariantInput] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors },
        watch,
        getValues,
    } = useForm({
                    defaultValues: {
                        sku: '',
                        name: '',
                        description: '',
                        is_active: true,
                        threshold_foam: 0,
                        threshold_carbon_vinyl: 0,
                        threshold_black_vinyl: 0,
                        threshold_diamond_vinyl: 0,
                        carbon_part_requirements: [{ part_type_id: '', quantity_required: 1 }],
                        standard_part_requirements: [{ part_type_id: '', quantity_required: 1 }],
                        custom_variants: {}
                    }
                });

    const {
        fields: carbonFields,
        append: appendCarbon,
        remove: removeCarbon,
    } = useFieldArray({
                          control,
                          name: 'carbon_part_requirements',
                      });

    const {
        fields: standardFields,
        append: appendStandard,
        remove: removeStandard,
    } = useFieldArray({
                          control,
                          name: 'standard_part_requirements',
                      });

    // Create custom variant field arrays using a fixed hook pattern
    // This uses a maximum of 10 potential custom variants to avoid the hooks order issue
    const customVariant1 = useFieldArray({ control, name: 'custom_variants.variant1' });
    const customVariant2 = useFieldArray({ control, name: 'custom_variants.variant2' });
    const customVariant3 = useFieldArray({ control, name: 'custom_variants.variant3' });
    const customVariant4 = useFieldArray({ control, name: 'custom_variants.variant4' });
    const customVariant5 = useFieldArray({ control, name: 'custom_variants.variant5' });
    const customVariant6 = useFieldArray({ control, name: 'custom_variants.variant6' });
    const customVariant7 = useFieldArray({ control, name: 'custom_variants.variant7' });
    const customVariant8 = useFieldArray({ control, name: 'custom_variants.variant8' });
    const customVariant9 = useFieldArray({ control, name: 'custom_variants.variant9' });
    const customVariant10 = useFieldArray({ control, name: 'custom_variants.variant10' });

    // Map the variant names to their respective field arrays
    const customVariantFieldArrays = useMemo(() => {
        const variantHooks = [
            customVariant1, customVariant2, customVariant3, customVariant4, customVariant5,
            customVariant6, customVariant7, customVariant8, customVariant9, customVariant10
        ];

        const fieldArrayMap = {};
        customVariants.forEach((variant, index) => {
            if (index < 10) { // Limit to 10 variants
                fieldArrayMap[variant] = variantHooks[index];
            }
        });

        return fieldArrayMap;
    }, [
                                                 customVariants,
                                                 customVariant1, customVariant2, customVariant3, customVariant4, customVariant5,
                                                 customVariant6, customVariant7, customVariant8, customVariant9, customVariant10
                                             ]);

    // Fetch part types and product data (if in edit mode)
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch part types
                const partTypesResponse = await partTypeService.getPartTypes();
                setPartTypes(partTypesResponse.data || []);

                // If in edit mode, fetch product data
                if (isEditMode) {
                    const productResponse = await productService.getProduct(id);
                    const product = productResponse.data;

                    // Set form values
                    setValue('sku', product.sku);
                    setValue('name', product.name || '');
                    setValue('description', product.description || '');
                    setValue('is_active', product.is_active);
                    setValue('threshold_foam', product.threshold_foam || 0);
                    setValue('threshold_carbon_vinyl', product.threshold_carbon_vinyl || 0);
                    setValue('threshold_black_vinyl', product.threshold_black_vinyl || 0);
                    setValue('threshold_diamond_vinyl', product.threshold_diamond_vinyl || 0);

                    // Set part requirements
                    if (product.part_requirements && product.part_requirements.length > 0) {
                        // Get unique variants
                        const variants = [...new Set(product.part_requirements.map(req => req.variant))];

                        // Filter out standard and carbon for custom variants
                        const customVariantsList = variants.filter(v => v !== 'carbon' && v !== 'standard');

                        // Limit to 10 variants
                        const limitedVariants = customVariantsList.slice(0, 10);
                        setCustomVariants(limitedVariants);

                        // Prepare form data for custom variants
                        const customVariantsData = {};
                        limitedVariants.forEach((variant, index) => {
                            // Map each variant to variant1, variant2, etc. in the form data
                            const formVariantKey = `variant${index + 1}`;

                            // Map the actual variant name to the form field name
                            const requirements = product.part_requirements
                                                        .filter(req => req.variant === variant)
                                                        .map(req => ({
                                                            id: req.id,
                                                            part_type_id: req.part_type_id.toString(),
                                                            quantity_required: req.quantity_required,
                                                        }));

                            customVariantsData[formVariantKey] = requirements;
                        });

                        // Separate carbon and standard requirements
                        const carbonRequirements = product.part_requirements
                                                          .filter(req => req.variant === 'carbon')
                                                          .map(req => ({
                                                              id: req.id,
                                                              part_type_id: req.part_type_id.toString(),
                                                              quantity_required: req.quantity_required,
                                                          }));

                        const standardRequirements = product.part_requirements
                                                            .filter(req => req.variant === 'standard')
                                                            .map(req => ({
                                                                id: req.id,
                                                                part_type_id: req.part_type_id.toString(),
                                                                quantity_required: req.quantity_required,
                                                            }));

                        // Reset form with all variants
                        const formData = {
                            ...getValues(),
                            carbon_part_requirements: carbonRequirements.length > 0 ? carbonRequirements : [{ part_type_id: '', quantity_required: 1 }],
                            standard_part_requirements: standardRequirements.length > 0 ? standardRequirements : [{ part_type_id: '', quantity_required: 1 }],
                            custom_variants: customVariantsData
                        };

                        reset(formData);
                    }
                }

                setError(null);
            } catch (err) {
                console.error('Error fetching form data:', err);
                setError('Failed to load form data. Please try again later.');
                toast.error('Failed to load form data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, isEditMode, setValue, reset, getValues]);

    // Add a new custom variant
    const handleAddCustomVariant = () => {
        if (!newVariantName.trim()) {
            toast.error('Please enter a variant name');
            return;
        }

        // Format the variant name (lowercase, replace spaces with underscores)
        const formattedVariantName = newVariantName.trim().toLowerCase().replace(/\s+/g, '_');

        // Check if variant already exists
        if (customVariants.includes(formattedVariantName) ||
            formattedVariantName === 'carbon' ||
            formattedVariantName === 'standard') {
            toast.error('This variant already exists');
            return;
        }

        // Check if we've reached the maximum number of variants
        if (customVariants.length >= 10) {
            toast.error('Maximum of 10 custom variants reached');
            return;
        }

        // Add to custom variants
        setCustomVariants([...customVariants, formattedVariantName]);

        // Initialize field array for this variant
        // Use the appropriate variant slot based on current count
        const variantIndex = customVariants.length;
        setValue(`custom_variants.variant${variantIndex + 1}`, [{ part_type_id: '', quantity_required: 1 }]);

        // Reset input
        setNewVariantName('');
        setShowNewVariantInput(false);
    };

    // Remove a custom variant
    const handleRemoveCustomVariant = (variant) => {
        if (!window.confirm(`Are you sure you want to remove the "${variant}" variant?`)) {
            return;
        }

        // Find the index of the variant to remove
        const variantIndex = customVariants.indexOf(variant);

        // Remove from custom variants list
        const updatedVariants = customVariants.filter(v => v !== variant);
        setCustomVariants(updatedVariants);

        // Get current form data
        const customVariantsData = { ...getValues().custom_variants };

        // Remap the variants to maintain continuous numbering
        const newCustomVariantsData = {};
        updatedVariants.forEach((variant, index) => {
            const formVariantKey = `variant${index + 1}`;
            // Find the original key for this variant
            const originalIndex = customVariants.indexOf(variant);
            const originalKey = `variant${originalIndex + 1}`;

            // Copy the data from its original position
            newCustomVariantsData[formVariantKey] = customVariantsData[originalKey];
        });

        // Update the form with the remapped data
        setValue('custom_variants', newCustomVariantsData);
    };

    // Convert the variant names for submission
    const getVariantName = (index) => {
        const variantIndex = index - 1; // because variant names are 1-indexed
        return customVariants[variantIndex] || `variant${index}`;
    };

    // Submit handler
    const onSubmit = async (data) => {
        try {
            setSubmitting(true);

            // Prepare part requirements
            const part_requirements = [];

            // Add carbon requirements
            data.carbon_part_requirements.forEach(req => {
                if (req.part_type_id) {
                    part_requirements.push({
                                               id: req.id, // Will be undefined for new requirements
                                               part_type_id: parseInt(req.part_type_id),
                                               quantity_required: parseInt(req.quantity_required),
                                               variant: 'carbon',
                                           });
                }
            });

            // Add standard requirements
            data.standard_part_requirements.forEach(req => {
                if (req.part_type_id) {
                    part_requirements.push({
                                               id: req.id, // Will be undefined for new requirements
                                               part_type_id: parseInt(req.part_type_id),
                                               quantity_required: parseInt(req.quantity_required),
                                               variant: 'standard',
                                           });
                }
            });

            // Add custom variant requirements
            if (data.custom_variants) {
                Object.entries(data.custom_variants).forEach(([formVariantKey, requirements]) => {
                    // Extract the variant number from the key (e.g., "variant1" -> 1)
                    const variantNumber = parseInt(formVariantKey.replace('variant', ''));
                    // Get the real variant name
                    const variantName = getVariantName(variantNumber);

                    requirements.forEach(req => {
                        if (req.part_type_id) {
                            part_requirements.push({
                                                       id: req.id, // Will be undefined for new requirements
                                                       part_type_id: parseInt(req.part_type_id),
                                                       quantity_required: parseInt(req.quantity_required),
                                                       variant: variantName,
                                                   });
                        }
                    });
                });
            }

            // Prepare product data
            const productData = {
                sku: data.sku,
                name: data.name,
                description: data.description,
                is_active: data.is_active,
                threshold_foam: parseInt(data.threshold_foam),
                threshold_carbon_vinyl: parseInt(data.threshold_carbon_vinyl),
                threshold_black_vinyl: parseInt(data.threshold_black_vinyl),
                threshold_diamond_vinyl: parseInt(data.threshold_diamond_vinyl),
                part_requirements,
            };

            let response;
            if (isEditMode) {
                response = await productService.updateProduct(id, productData);
                toast.success('Product updated successfully');
            } else {
                response = await productService.createProduct(productData);
                toast.success('Product created successfully');
            }

            // Navigate to product details page
            navigate(`/products/${response.data.id || id}`);
        } catch (err) {
            console.error('Error saving product:', err);
            toast.error(err.response?.data?.message || 'Failed to save product');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    // Helper function to get the form field name for a specific variant
    const getVariantFieldName = (variantName) => {
        const index = customVariants.indexOf(variantName);
        return `variant${index + 1}`;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <Link to="/products" className="text-gray-500 hover:text-gray-700">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Link>
                    <h1 className="text-2xl font-semibold text-gray-800">
                        {isEditMode ? 'Edit Product' : 'Create Product'}
                    </h1>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="bg-white shadow-md rounded-lg p-6 space-y-8">
                {/* Product Details Section */}
                <div className="space-y-4">
                    <h2 className="text-lg font-medium text-gray-900">Product Details</h2>

                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        {/* SKU */}
                        <div className="sm:col-span-3">
                            <label htmlFor="sku" className="block text-sm font-medium text-gray-700">
                                SKU *
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    id="sku"
                                    {...register('sku', { required: 'SKU is required' })}
                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 ${
                                        errors.sku ? 'border-red-300' : ''
                                    }`}
                                    placeholder="RV-MONT-HIGH-1"
                                     // SKU cannot be changed in edit mode
                                />
                                {errors.sku && (
                                    <p className="mt-1 text-sm text-red-600">{errors.sku.message}</p>
                                )}
                            </div>
                        </div>

                        {/* Name */}
                        <div className="sm:col-span-3">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Name
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    id="name"
                                    {...register('name')}
                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3"
                                    placeholder="Product Name"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="sm:col-span-6">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <div className="mt-1">
                                <textarea
                                    id="description"
                                    rows={3}
                                    {...register('description')}
                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3"
                                    placeholder="Product description"
                                />
                            </div>
                        </div>

                        {/* Status */}
                        <div className="sm:col-span-3">
                            <div className="flex items-center">
                                <input
                                    id="is_active"
                                    type="checkbox"
                                    {...register('is_active')}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_active" className="ml-2 block text-sm font-medium text-gray-700">
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className="sm:col-span-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 w-full">Thresholds</h3>
                        </div>
                        {/* Thresholds */}
                        <div className="sm:col-span-3">
                            <label htmlFor="threshold_foam" className="block text-sm font-medium text-gray-700">
                                Foam Threshold
                            </label>
                            <div className="mt-1">
                                <input
                                    type="number"
                                    id="threshold_foam"
                                    {...register('threshold_foam', {
                                        valueAsNumber: true,
                                        min: { value: 0, message: 'Threshold must be at least 0' }
                                    })}
                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 ${
                                        errors.threshold_foam ? 'border-red-300' : ''
                                    }`}
                                    placeholder="0"
                                />
                                {errors.threshold_foam && (
                                    <p className="mt-1 text-sm text-red-600">{errors.threshold_foam.message}</p>
                                )}
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            <label htmlFor="threshold_carbon_vinyl" className="block text-sm font-medium text-gray-700">
                                Carbon Threshold
                            </label>
                            <div className="mt-1">
                                <input
                                    type="number"
                                    id="threshold_carbon_vinyl"
                                    {...register('threshold_carbon_vinyl', {
                                        valueAsNumber: true,
                                        min: { value: 0, message: 'Threshold must be at least 0' }
                                    })}
                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 ${
                                        errors.threshold_carbon_vinyl ? 'border-red-300' : ''
                                    }`}
                                    placeholder="0"
                                />
                                {errors.threshold_carbon_vinyl && (
                                    <p className="mt-1 text-sm text-red-600">{errors.threshold_carbon_vinyl.message}</p>
                                )}
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            <label htmlFor="threshold_black_vinyl" className="block text-sm font-medium text-gray-700">
                                Standard (Black) Threshold
                            </label>
                            <div className="mt-1">
                                <input
                                    type="number"
                                    id="threshold_black_vinyl"
                                    {...register('threshold_black_vinyl', {
                                        valueAsNumber: true,
                                        min: { value: 0, message: 'Threshold must be at least 0' }
                                    })}
                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 ${
                                        errors.threshold_black_vinyl ? 'border-red-300' : ''
                                    }`}
                                    placeholder="0"
                                />
                                {errors.threshold_black_vinyl && (
                                    <p className="mt-1 text-sm text-red-600">{errors.threshold_black_vinyl.message}</p>
                                )}
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            <label htmlFor="threshold_diamond_vinyl" className="block text-sm font-medium text-gray-700">
                                Diamond (Back) Threshold
                            </label>
                            <div className="mt-1">
                                <input
                                    type="number"
                                    id="threshold_diamond_vinyl"
                                    {...register('threshold_diamond_vinyl', {
                                        valueAsNumber: true,
                                        min: { value: 0, message: 'Threshold must be at least 0' }
                                    })}
                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 ${
                                        errors.threshold_diamond_vinyl ? 'border-red-300' : ''
                                    }`}
                                    placeholder="0"
                                />
                                {errors.threshold_diamond_vinyl && (
                                    <p className="mt-1 text-sm text-red-600">{errors.threshold_diamond_vinyl.message}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Carbon Part Requirements Section */}
                <PartRequirementsSection
                    title="Carbon Variant Part Requirements"
                    fields={carbonFields}
                    append={appendCarbon}
                    remove={removeCarbon}
                    register={register}
                    errors={errors}
                    partTypes={partTypes}
                    baseName="carbon_part_requirements"
                />

                {/* Standard Part Requirements Section */}
                <PartRequirementsSection
                    title="Standard Variant Part Requirements"
                    fields={standardFields}
                    append={appendStandard}
                    remove={removeStandard}
                    register={register}
                    errors={errors}
                    partTypes={partTypes}
                    baseName="standard_part_requirements"
                />

                {/* Custom Variants Section - Modified to use fixed form fields */}
                <div className="space-y-4 border-t border-gray-200 pt-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-medium text-gray-900">Custom Variant Part Requirements</h2>

                        {showNewVariantInput ? (
                            <div className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={newVariantName}
                                    onChange={(e) => setNewVariantName(e.target.value)}
                                    placeholder="Enter variant name"
                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block sm:text-sm border-gray-300 rounded-md p-2"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddCustomVariant}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                    Add
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowNewVariantInput(false);
                                        setNewVariantName('');
                                    }}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                             <button
                                 type="button"
                                 onClick={() => setShowNewVariantInput(true)}
                                 disabled={customVariants.length >= 10}
                                 className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                             >
                                 {customVariants.length >= 10 ? 'Maximum Variants Reached' : 'Add Custom Variant'}
                             </button>
                         )}
                    </div>

                    {customVariants.length === 0 ? (
                        <div className="bg-gray-50 p-6 text-center rounded-md">
                            <p className="text-gray-500">
                                No custom variants added. Click "Add Custom Variant" to create one.
                            </p>
                        </div>
                    ) : (
                         <div className="space-y-8">
                             {customVariants.map((variant, index) => {
                                 const formVariantKey = `variant${index + 1}`;
                                 const fieldArray = customVariantFieldArrays[variant];

                                 if (!fieldArray) return null;

                                 return (
                                     <div key={variant} className="space-y-4 border border-gray-200 rounded-md p-4">
                                         <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                             <h3 className="text-md font-medium text-gray-900 capitalize">
                                                 {variant.replace(/_/g, ' ')} Variant
                                             </h3>
                                             <div className="flex items-center space-x-2">
                                                 <button
                                                     type="button"
                                                     onClick={() => {
                                                         fieldArray.append({ part_type_id: '', quantity_required: 1 });
                                                     }}
                                                     className="inline-flex items-center px-2 py-1 text-xs border border-transparent font-medium rounded text-primary-600 hover:text-primary-800 focus:outline-none"
                                                 >
                                                     Add Part
                                                 </button>
                                                 <button
                                                     type="button"
                                                     onClick={() => handleRemoveCustomVariant(variant)}
                                                     className="inline-flex items-center px-2 py-1 text-xs border border-transparent font-medium rounded text-red-600 hover:text-red-800 focus:outline-none"
                                                 >
                                                     Remove Variant
                                                 </button>
                                             </div>
                                         </div>

                                         <div className="overflow-x-auto">
                                             <table className="min-w-full divide-y divide-gray-200">
                                                 <thead className="bg-gray-50">
                                                 <tr>
                                                     <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                         Part Type
                                                     </th>
                                                     <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Quantity Required
                                                        </th>
                                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Action
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {fieldArray.fields.map((field, fieldIndex) => (
                                                        <tr key={field.id}>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <select
                                                                    {...register(`custom_variants.${formVariantKey}.${fieldIndex}.part_type_id`, {
                                                                        required: 'Part type is required'
                                                                    })}
                                                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                                                                        errors?.custom_variants?.[formVariantKey]?.[fieldIndex]?.part_type_id ? 'border-red-300' : ''
                                                                    }`}
                                                                >
                                                                    <option value="">Select Part Type</option>
                                                                    {partTypes.map(partType => (
                                                                        <option key={partType.id} value={partType.id}>
                                                                            {partType.name} {partType.is_sku_specific ? '(SKU-specific)' : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {errors?.custom_variants?.[formVariantKey]?.[fieldIndex]?.part_type_id && (
                                                                    <p className="mt-1 text-sm text-red-600">
                                                                        {errors.custom_variants[formVariantKey][fieldIndex].part_type_id.message}
                                                                    </p>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <input
                                                                    type="number"
                                                                    {...register(`custom_variants.${formVariantKey}.${fieldIndex}.quantity_required`, {
                                                                        required: 'Quantity is required',
                                                                        min: { value: 1, message: 'Quantity must be at least 1' },
                                                                        valueAsNumber: true
                                                                    })}
                                                                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                                                                        errors?.custom_variants?.[formVariantKey]?.[fieldIndex]?.quantity_required ? 'border-red-300' : ''
                                                                    }`}
                                                                    placeholder="1"
                                                     />
                                                     {errors?.custom_variants?.[formVariantKey]?.[fieldIndex]?.quantity_required && (
                                                         <p className="mt-1 text-sm text-red-600">
                                                             {errors.custom_variants[formVariantKey][fieldIndex].quantity_required.message}
                                                         </p>
                                                     )}
                                                 </td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-right">
                                                     <button
                                                         type="button"
                                                         onClick={() => fieldArray.remove(fieldIndex)}
                                                         className="text-red-600 hover:text-red-900"
                                                     >
                                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                             <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                         </svg>
                                                     </button>
                                                 </td>
                                                 </tr>
                                                 ))}
                                                 {fieldArray.fields.length === 0 && (
                                                     <tr>
                                                         <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                                                             No part requirements added for this variant. Click "Add Part" to add one.
                                                         </td>
                                                     </tr>
                                                 )}
                                             </tbody>
                                         </table>
                                     </div>
                             </div>
                             );
                             })}
                         </div>
                    )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200">
                    <Link
                        to="/products"
                        className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                        {submitting ? 'Saving...' : isEditMode ? 'Update Product' : 'Create Product'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProductForm;
